import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import { LogIn, Info, ArrowRight, X, BookOpen, Users, Award, Clock } from 'lucide-react';

import { useUser } from '../contexts/UserContext';
import { authErrorMessage } from '../auth/errors';
import AccountRecovery, { AuthLoading } from './AccountRecovery';

const images = [
  {
    src: "/images/stock-delivery-photo.jpg",
    alt: "Doctor consulting with a patient in a modern clinic setting",
    caption: "Clinical consultation skills are essential for healthcare professionals"
  },
  {
    src: "/images/lab-students.jpg",
    alt: "Students working in a medical research laboratory",
    caption: "Hands-on laboratory experience prepares students for research roles"
  },
  {
    src: "/images/PatientMonitorDevice.jpg",
    alt: "Modern healthcare technology in use",
    caption: "Technology proficiency is increasingly important in patient care"
  },
  {
    src: "/images/tooth-shades.jpg",
    alt: "Dental healthcare professional at work",
    caption: "Specialized skills development for various healthcare disciplines"
  },
  {
    src: "/images/MentoringClass.jpg",
    alt: "Healthcare mentoring session with students",
    caption: "Mentorship creates lasting professional development relationships"
  }
];

const features = [
  {
    icon: <BookOpen className="h-6 w-6 text-cyan-300" />,
    title: "Personalized Learning Paths",
    description: "Tailored curriculum based on your career goals and current skill level"
  },
  {
    icon: <Users className="h-6 w-6 text-cyan-300" />,
    title: "Mentor Matching",
    description: "Connect with experienced professionals in your field of interest"
  },
  {
    icon: <Award className="h-6 w-6 text-cyan-300" />,
    title: "Skill Certification",
    description: "Earn credentials recognized by leading healthcare institutions"
  },
  {
    icon: <Clock className="h-6 w-6 text-cyan-300" />,
    title: "Flexible Scheduling",
    description: "Learn and practice at your own pace with 24/7 platform access"
  }
];

const testimonials = [
  {
    quote: "Pathway2Practice transformed my career trajectory in just 6 months.",
    author: "Dr. Sarah Chen",
    role: "Pediatric Specialist"
  },
  {
    quote: "The mentor matching system connected me with professionals I would never have met otherwise.",
    author: "Michael Rodriguez",
    role: "Medical Student"
  }
];

const LandingPage = () => {
  const { user, loading, error, login, loginWithGoogle, loginAsGuest, resetPassword } = useFirebase();
  const { profileStatus } = useUser();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('students');
  const [animationClass, setAnimationClass] = useState('');
  
  // Added new state variables for password reset functionality
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailError, setResetEmailError] = useState('');
  const [resetEmailSuccess, setResetEmailSuccess] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    if (loading || !user || profileStatus === 'loading' || profileStatus === 'error') return;
    if (profileStatus === 'missing' || profileStatus === 'incomplete') {
      navigate('/register?step=2', { replace: true });
    } else if (profileStatus === 'ready' || profileStatus === 'guest') {
      navigate(user.isAnonymous ? '/app' : '/app/profile', { replace: true });
    }
  }, [user, loading, profileStatus, navigate]);

  useEffect(() => {
    // Add entrance animation class after component mounts
    setAnimationClass('fade-in-up');
    
    // Auto-rotate images - slowed down to 8 seconds
    const intervalId = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 8000); // Changed from 5000ms to 8000ms (8 seconds)
    
    return () => clearInterval(intervalId);
  }, []);

  // Taller images with reduced horizontal overlap
  const getImageStyle = (index) => {
    const isActive = index === currentImageIndex;
    const isPrev = index === (currentImageIndex - 1 + images.length) % images.length;
    const isNext = index === (currentImageIndex + 1) % images.length;
    
    let zIndex = 0;
    let translateX = '0%';
    let translateY = '0%';
    
    if (isActive) {
      zIndex = 30;
    } else if (isPrev) {
      zIndex = 20;
      translateX = '-40%'; // Reduced horizontal overlap
      translateY = '5%'; // Slight vertical offset
    } else if (isNext) {
      zIndex = 20;
      translateX = '40%'; // Reduced horizontal overlap
      translateY = '5%'; // Slight vertical offset
    }
    
    return {
      zIndex,
      transform: `translate(${translateX}, ${translateY}) scale(${isActive ? '0.95' : isPrev || isNext ? '0.8' : '0.65'})`,
      opacity: isActive ? 1 : isPrev || isNext ? 0.8 : 0,
      transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)',
      height: isActive ? '100%' : '85%', // Keep active image taller
    };
  };

  const handleLogin = () => {
    setShowLoginModal(true);
  };

  if (loading || profileStatus === 'loading') return <AuthLoading />;
  if (error || profileStatus === 'error') return <AccountRecovery />;

  const signIn = async action => {
    if (isLoading) return;
    setLoginError('');
    setIsLoading(true);
    try { await action(); }
    catch (error) { setLoginError(authErrorMessage(error)); }
    finally { setIsLoading(false); }
  };
  const handleLoginSubmit = e => {
    e.preventDefault();
    return signIn(() => login(username, password));
  };
  const handleGoogleSignIn = () => signIn(loginWithGoogle);
  const handleContinueAsGuest = () => signIn(loginAsGuest);
  const handleRegister = () => navigate('/register');

  const handleForgotPassword = e => {
    e.preventDefault();
    setShowLoginModal(false);
    setResetEmail(username);
    setResetEmailError('');
    setResetEmailSuccess('');
    setShowPasswordResetModal(true);
  };
  const handlePasswordReset = async e => {
    e.preventDefault();
    if (isResetting) return;
    setResetEmailError('');
    setResetEmailSuccess('');
    if (!resetEmail.trim()) {
      setResetEmailError('Please enter your email address.');
      return;
    }
    setIsResetting(true);
    try {
      await resetPassword(resetEmail);
      setResetEmailSuccess('If an account uses this email, a password reset link has been sent. Check your inbox and spam folder.');
    } catch (error) { setResetEmailError(authErrorMessage(error)); }
    finally { setIsResetting(false); }
  };

  // Function to determine which user type tab is active
  const isTabActive = (tab) => activeTab === tab;

  return (
    <div className="relative flex flex-col min-h-screen overflow-hidden">
      {/* Distinctive geometric background pattern */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Custom branded background with unique geometric pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-800 via-blue-600 to-cyan-500">
          {/* Branded geometric elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute top-1/2 right-0 w-1/4 h-1/2 bg-cyan-300 rounded-full blur-3xl translate-x-1/3"></div>
            <div className="absolute bottom-0 left-1/2 w-1/3 h-1/3 bg-blue-300 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>
          </div>
          
          {/* Dynamic grid background that's more distinctive */}
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px),
              radial-gradient(circle, rgba(255,255,255,0.1) 2px, transparent 2px)
            `,
            backgroundSize: '40px 40px, 40px 40px, 120px 120px',
            backgroundPosition: '0 0, 0 0, 20px 20px'
          }}></div>
        </div>
      </div>

      <div className="relative z-10 flex flex-col min-h-screen">
        <header className="w-full bg-white bg-opacity-10 backdrop-filter backdrop-blur-md border-b border-white border-opacity-20">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center">
              {/* Logo with distinctive icon */}
              <div className="flex items-center">
                <div className="mr-2 bg-white rounded-full p-1">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-cyan-400 rounded-full flex items-center justify-center">
                    <span className="font-bold text-lg text-white">P2P</span>
                  </div>
                </div>
                <h1 className="text-4xl font-bold text-white tracking-tight">
                  Pathway<span className="text-cyan-300">2</span>Practice
                </h1>
              </div>
            </div>
            
            {/* Navigation links */}
            <nav className="hidden md:flex items-center space-x-6">
              <a href="#features" className="text-white hover:text-cyan-300 transition-colors">Features</a>
              <a href="#testimonials" className="text-white hover:text-cyan-300 transition-colors">Testimonials</a>
              <a href="#about" className="text-white hover:text-cyan-300 transition-colors">About Us</a>
              <button 
                onClick={handleLogin}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-all duration-300"
              >
                Login
              </button>
            </nav>
          </div>
        </header>
        
        <main className="flex-grow">
          {/* Hero section with improved branding and information architecture */}
          <section className="relative min-h-[85vh] overflow-hidden">
            <div className="absolute inset-0 z-0">
              {/* Enhanced image display */}
              {images.map((image, index) => (
                <div 
                  key={index}
                  className="absolute inset-0 transition-opacity duration-1000"
                  style={{
                    opacity: index === currentImageIndex ? 1 : 0,
                    zIndex: index === currentImageIndex ? 1 : 0
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-800/70 to-blue-900/60"></div>
                  <img 
                    src={image.src} 
                    alt={image.alt} 
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              ))}
            </div>

            {/* Content grid with more distinct zones */}
            <div className="relative z-10 grid md:grid-cols-12 h-full min-h-[85vh] pt-20 max-w-7xl mx-auto">
              {/* Left side: More distinctive branding and information */}
              <div className="md:col-span-7 flex flex-col justify-center px-6 md:px-10">
                <div className={`${animationClass} max-w-xl`} style={{animationDelay: '0ms'}}>
                  {/* Image indicators - moved to be near the content */}
                  <div className="flex space-x-2 mb-8">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-3 h-3 rounded-full transition-all ${
                          index === currentImageIndex ? 'bg-cyan-300 scale-100' : 'bg-white/40 scale-75'
                        }`}
                        aria-label={`View image ${index + 1}`}
                      />
                    ))}
                  </div>
                  
                  <h2 className="text-5xl font-bold text-white leading-tight mb-4">
                    Shape Your <span className="text-cyan-300">Healthcare</span> Career Journey
                  </h2>
                  
                  <p className="text-xl text-white/90 mb-6 leading-relaxed">
                    Access personalized learning paths, connect with experienced mentors, and track your progress toward your healthcare career goals.
                  </p>
                  
                  {/* Image caption with additional context */}
                  <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 mb-8 transform transition-all duration-500" style={{
                    opacity: 1,
                    transform: `translateY(0px)`
                  }}>
                    <p className="text-white/90 italic">"{images[currentImageIndex].caption}"</p>
                  </div>
                  
                  {/* Audience toggle tabs */}
                  <div className="hidden md:block mb-8">
                    <div className="inline-flex bg-white/10 backdrop-blur-sm rounded-xl p-1">
                      <button 
                        className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${isTabActive('students') ? 'bg-white text-blue-600' : 'text-white hover:bg-white/10'}`}
                        onClick={() => setActiveTab('students')}
                      >
                        For Students
                      </button>
                      <button 
                        className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${isTabActive('mentors') ? 'bg-white text-blue-600' : 'text-white hover:bg-white/10'}`}
                        onClick={() => setActiveTab('mentors')}
                      >
                        For Mentors
                      </button>
                      <button 
                        className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${isTabActive('institutions') ? 'bg-white text-blue-600' : 'text-white hover:bg-white/10'}`}
                        onClick={() => setActiveTab('institutions')}
                      >
                        For Institutions
                      </button>
                    </div>
                    
                    <div className="mt-4">
                      {isTabActive('students') && (
                        <p className="text-white/90">Track your clinical hours, access structured learning paths, and connect with experienced mentors.</p>
                      )}
                      {isTabActive('mentors') && (
                        <p className="text-white/90">Share your expertise, guide the next generation of healthcare providers, and build your professional network.</p>
                      )}
                      {isTabActive('institutions') && (
                        <p className="text-white/90">Streamline clinical rotations, standardize assessment, and ensure comprehensive skills development.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right side form container */}
              <div className="md:col-span-5 flex items-center justify-center px-6 pb-20">
                <div className={`${animationClass} bg-white/15 backdrop-blur-lg rounded-2xl p-8 border border-white/30 shadow-xl w-full max-w-md`} style={{animationDelay: '300ms'}}>
                  <div className="text-center mb-6">
                    <h2 className="text-3xl font-bold text-white mb-2">Begin Your Journey</h2>
                    <p className="text-white text-opacity-90">
                      Join thousands of healthcare professionals advancing their careers.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    {loginError && !showLoginModal && <p className="bg-red-50 text-red-700 p-3 rounded-lg" role="alert">{loginError}</p>}
                    <button 
                      onClick={handleLogin} 
                      className="bg-white w-full text-blue-600 px-6 py-3 rounded-xl font-medium shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-blue-50"
                    >
                      <LogIn className="mr-2 h-5 w-5" /> 
                      Login
                    </button>
                    
                    <button 
                      onClick={handleRegister} 
                      className="bg-gradient-to-r from-emerald-500 to-green-400 w-full text-white px-6 py-3 rounded-xl font-medium shadow-lg flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:from-emerald-600 hover:to-green-500"
                    >
                      Register
                    </button>

                    <button
                      onClick={handleGoogleSignIn}
                      disabled={isLoading}
                      className="w-full bg-white border border-[#dadce0] text-[#3c4043] px-6 py-3 rounded-xl font-medium shadow-[0_1px_2px_rgba(60,64,67,0.30),0_1px_3px_1px_rgba(60,64,67,0.15)] flex items-center justify-center transition-all duration-300 hover:shadow-[0_1px_3px_rgba(60,64,67,0.30),0_4px_8px_3px_rgba(60,64,67,0.15)]"
                    >
                      <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.72 1.22 9.22 3.6l6.88-6.88C35.91 2.39 30.38 0 24 0 14.64 0 6.56 5.38 2.55 13.22l8 6.2C12.45 13.72 17.74 9.5 24 9.5z" />
                        <path fill="#4285F4" d="M46.5 24.55c0-1.63-.15-3.2-.42-4.72H24v8.95h12.68c-.55 2.96-2.22 5.47-4.73 7.15l7.64 5.93C44.05 37.73 46.5 31.75 46.5 24.55z" />
                        <path fill="#FBBC05" d="M10.55 28.58a14.46 14.46 0 0 1 0-9.16l-8-6.2A24 24 0 0 0 0 24c0 3.86.93 7.51 2.55 10.78l8-6.2z" />
                        <path fill="#34A853" d="M24 48c6.48 0 11.91-2.13 15.88-5.8l-7.64-5.93c-2.13 1.43-4.86 2.28-8.24 2.28-6.26 0-11.55-4.22-13.45-9.92l-8 6.2C6.56 42.62 14.64 48 24 48z" />
                      </svg>
                      Continue with Google
                    </button>
                    
                    <div className="relative py-3">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/20"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="px-3 text-sm text-white bg-blue-500/40 rounded-full">or</span>
                      </div>
                    </div>
                    
                    <button 
                      onClick={handleContinueAsGuest} disabled={isLoading}
                      className="bg-transparent border border-white/40 w-full text-white px-6 py-3 rounded-xl font-medium flex items-center justify-center transition-all duration-300 hover:bg-white/10"
                    >
                      Continue as guest
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </button>
                  </div>
                  
                  {/* Trust indicators */}
                  <div className="mt-8 pt-6 border-t border-white/20">
                    <p className="text-white/70 text-xs text-center mb-3">Trusted by leading healthcare institutions</p>
                    <div className="flex justify-between items-center">
                      <div className="text-white/60 text-xs font-semibold">ACME MEDICAL</div>
                      <div className="text-white/60 text-xs font-semibold">HEALTH UNIVERSITY</div>
                      <div className="text-white/60 text-xs font-semibold">CARE NETWORK</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          {/* Features section - Adding more distinct zones for organization */}
          <section id="features" className="py-20 bg-gradient-to-b from-blue-900 to-blue-800 relative">
            <div className="max-w-7xl mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-white mb-4">Platform Features</h2>
                <p className="text-xl text-white/70 max-w-2xl mx-auto">Everything you need to accelerate your healthcare career journey</p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {features.map((feature, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 transition-all duration-300 hover:bg-white/15 hover:transform hover:scale-105">
                    <div className="mb-4 bg-blue-900/50 rounded-full w-12 h-12 flex items-center justify-center">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-white/70">{feature.description}</p>
                  </div>
                ))}
              </div>
              
              {/* Stats for social proof */}
              <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-cyan-300 mb-2">2,500+</div>
                  <div className="text-white/70">Active Students</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-cyan-300 mb-2">350+</div>
                  <div className="text-white/70">Mentor Experts</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-cyan-300 mb-2">50+</div>
                  <div className="text-white/70">Partner Institutions</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-cyan-300 mb-2">95%</div>
                  <div className="text-white/70">Career Placement</div>
                </div>
              </div>
            </div>
          </section>
          
          {/* Testimonials section */}
          <section id="testimonials" className="py-20 bg-gradient-to-b from-blue-800 to-blue-900 relative">
            <div className="max-w-7xl mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-white mb-4">Success Stories</h2>
                <p className="text-xl text-white/70 max-w-2xl mx-auto">Hear from healthcare professionals who've transformed their careers</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
                    <div className="text-xl italic text-white mb-6">"{testimonial.quote}"</div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-cyan-300 rounded-full flex items-center justify-center text-blue-900 font-bold text-lg">
                        {testimonial.author.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="font-medium text-white">{testimonial.author}</div>
                        <div className="text-white/70 text-sm">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          
          {/* About section */}
          <section id="about" className="py-20 bg-gradient-to-b from-blue-900 to-blue-800 relative">
            <div className="max-w-7xl mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-4xl font-bold text-white mb-6">About Pathway2Practice</h2>
                  <p className="text-lg text-white/80 mb-6">
                    Founded by healthcare educators and professionals, Pathway2Practice bridges the gap between classroom theory and clinical practice.
                  </p>
                  <p className="text-lg text-white/80 mb-6">
                    Our platform provides structured clinical skill development, mentorship opportunities, and progress tracking to ensure healthcare students are fully prepared for their careers.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center text-white">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center mr-3">
                        <div className="w-2 h-2 rounded-full bg-cyan-300"></div>
                      </div>
                      Established in 2018 by leading medical educators
                    </li>
                    <li className="flex items-center text-white">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center mr-3">
                        <div className="w-2 h-2 rounded-full bg-cyan-300"></div>
                      </div>
                      Partnered with 50+ healthcare institutions nationwide
                    </li>
                    <li className="flex items-center text-white">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center mr-3">
                        <div className="w-2 h-2 rounded-full bg-cyan-300"></div>
                      </div>
                      Recognized for innovation in healthcare education
                    </li>
                  </ul>
                </div>
                <div className="relative">
                  <div className="aspect-square rounded-2xl overflow-hidden border-8 border-white/10 shadow-2xl rotate-3 transform hover:rotate-0 transition-all duration-500">
                    <img src="/images/MentoringClass.jpg" alt="Healthcare education in action" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute -bottom-6 -left-6 bg-white/15 backdrop-blur-lg rounded-xl p-4 border border-white/30 shadow-xl max-w-xs">
                    <p className="text-white font-medium">"Our mission is to create confident, competent healthcare professionals through structured practice and mentorship."</p>
                    <p className="text-white/70 text-sm mt-2">- Dr. James Chen, Founder</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        
        <footer className="bg-blue-900 text-white py-12 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8 mb-12">
              <div>
                <div className="flex items-center mb-6">
                  <div className="mr-2 bg-white rounded-full p-1">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-cyan-400 rounded-full flex items-center justify-center">
                      <span className="font-bold text-lg text-white">P2P</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-white tracking-tight">
                    Pathway<span className="text-cyan-300">2</span>Practice
                  </h3>
                </div>
                <p className="text-white/70 mb-4">Transforming healthcare education through structured practice and mentorship.</p>
                <div className="flex space-x-4">
                  <a href="#" className="text-white/70 hover:text-cyan-300 transition-colors">
                    <span className="sr-only">Twitter</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a href="#" className="text-white/70 hover:text-cyan-300 transition-colors">
                    <span className="sr-only">LinkedIn</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  </a>
                  <a href="#" className="text-white/70 hover:text-cyan-300 transition-colors">
                    <span className="sr-only">Instagram</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Platform</h3>
                <ul className="space-y-3">
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Features</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Pricing</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Testimonials</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Success Stories</a></li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Resources</h3>
                <ul className="space-y-3">
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Blog</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Support</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Learning Center</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">FAQ</a></li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Company</h3>
                <ul className="space-y-3">
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">About Us</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Careers</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Contact</a></li>
                  <li>
                    <button
                      onClick={() => setShowDisclaimer(!showDisclaimer)}
                      className="text-white/70 hover:text-white transition-colors flex items-center"
                    >
                      <Info className="mr-1 h-4 w-4" />
                      Legal Information
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="pt-8 border-t border-white/10 text-center">
              <p className="text-white/60">&copy; {new Date().getFullYear()} Pathway2Practice. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>

      {/* Login Modal - Updated with password reset functionality */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Login to Pathway2Practice</h2>
              <button onClick={() => setShowLoginModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleLoginSubmit}>
              <div className="mb-4">
                <label htmlFor="username" className="block text-gray-700 text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="shadow-sm border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="password" className="block text-gray-700 text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="shadow-sm border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                />
              </div>
              {loginError && <p className="text-red-500 text-sm italic mb-4">{loginError}</p>}
              <div className="flex items-center justify-between">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${
                    isLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                  } text-white font-medium py-2 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors`}
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </button>
                {/* Changed from anchor to button */}
                <button 
                  type="button"
                  className="inline-block align-baseline font-medium text-sm text-blue-600 hover:text-blue-800" 
                  onClick={handleForgotPassword}
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal - New component */}
      {showPasswordResetModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Reset Your Password</h2>
              <button onClick={() => setShowPasswordResetModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handlePasswordReset}>
              <div className="mb-6">
                <label htmlFor="resetEmail" className="block text-gray-700 text-sm font-medium mb-2">
                  Enter your email address and we'll send you a link to reset your password
                </label>
                <input
                  type="email"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="shadow-sm border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="your-email@example.com"
                  required
                />
              </div>
              {resetEmailError && <p role="alert" className="text-red-500 text-sm italic mb-4">{resetEmailError}</p>}
              {resetEmailSuccess && <p role="status" className="text-green-500 text-sm italic mb-4">{resetEmailSuccess}</p>}
              <div className="flex items-center justify-between">
                <button
                  type="submit"
                  disabled={isResetting}
                  className={`${
                    isResetting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                  } text-white font-medium py-2 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors`}
                >
                  {isResetting ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setShowLoginModal(true);
                  }}
                  className="inline-block align-baseline font-medium text-sm text-blue-600 hover:text-blue-800"
                >
                  Back to Login
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Legal Disclaimer */}
      {showDisclaimer && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-2xl w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Terms & Privacy Information</h2>
              <button onClick={() => setShowDisclaimer(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <div className="prose max-w-none">
              <p>
                By continuing, you're stepping into a world of career growth and opportunities. 
                Your journey with Pathway2Practice is governed by our Terms of Service and Privacy Policy.
              </p>
              <h3>Terms of Service</h3>
              <p>
                Our platform is designed to connect healthcare students with mentors and educational resources.
                Users must be at least 18 years of age and agree not to misuse the platform or share account credentials.
              </p>
              <h3>Privacy Policy</h3>
              <p>
                We collect information to improve your experience and connect you with appropriate resources.
                Your data is secured using industry-standard encryption and is never sold to third parties.
              </p>
              <h3>Educational Disclaimer</h3>
              <p>
                Content on Pathway2Practice is for educational purposes only and does not replace formal medical training or certification.
                Always consult with your educational institution regarding requirements for clinical practice.
              </p>
            </div>
            <div className="mt-8 flex justify-center space-x-4">
              <button 
                onClick={() => setShowDisclaimer(false)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors duration-300"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .fade-in-up {
          animation: fadeIn 0.6s ease-out forwards;
        }
        /* Custom height class for taller images */
        .h-110 {
          height: 28rem;
        }
        /* Custom transition duration */
        .duration-2000 {
          transition-duration: 2005ms;
        }
        
        /* Responsive adjustments for mobile devices */
        @media (max-width: 768px) {
          .fade-in-up {
            animation-delay: 0ms !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
